import { ClipboardList, House, Search, User } from 'lucide-preact';
import type { LucideIcon } from 'lucide-preact';
import { route } from 'preact-router';

interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { icon: House, label: 'Home', path: '/' },
  { icon: Search, label: 'Search', path: '/search' },
  { icon: ClipboardList, label: 'Bookings', path: '/account/bookings' },
  { icon: User, label: 'Account', path: '/account' },
];

interface BottomNavProps {
  currentPath: string;
}

// 133: `item.path` alone matches a route only exactly, or as a full path segment beneath it —
// never a bare string prefix (so `/account` does not match a hypothetical `/accountability`).
function isPathMatch(currentPath: string, itemPath: string): boolean {
  if (itemPath === '/') return currentPath === '/';
  return currentPath === itemPath || currentPath.startsWith(`${itemPath}/`);
}

// Raising this z-50 breaks the date & time picker: its mobile sheet sits at z-[60]
// precisely to out-rank this bar, which is fixed to the same edge and comes later in
// the DOM. At an equal z-index the bar swallows the sheet's Confirm tap.
export function BottomNav({ currentPath }: BottomNavProps) {
  // 133: on a nested route (e.g. `/account/bookings`, under `/account`), more than one item can
  // path-match — the longest (most specific) match is the one that's actually active.
  const activeItem = NAV_ITEMS.filter((item) => isPathMatch(currentPath, item.path)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-surface border-t border-surface-mid flex z-50">
      {NAV_ITEMS.map((item) => {
        const isActive = item === activeItem;
        return (
          <button
            key={item.path}
            type="button"
            onClick={() => route(item.path)}
            className={`flex-1 flex flex-col items-center py-2 text-xs transition-colors ${
              isActive ? 'text-primary' : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <item.icon size={20} aria-hidden="true" />
            {item.label}
          </button>
        );
      })}
    </nav>
  );
}
